import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plan } from '@/lib/plans/types';
import { formatCurrency } from '@/lib/utils';
import { FEATURE_NAMES, SYSTEM_FIELD_CONFIG } from '@/lib/plans/enforce';
import { Check, Star, Zap, Crown, Gift } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DefaultFeaturesSection } from './DefaultFeaturesSection';

interface EnhancedPlanCardProps {
  plan: Plan;
  currentPlan?: any;
  systemCode: string;
  onSelect: (billingPeriod: 'monthly' | 'yearly') => void;
  disabled?: boolean;
}

export function EnhancedPlanCard({ plan, currentPlan, systemCode, onSelect, disabled = false }: EnhancedPlanCardProps) {
  const isCurrentPlan = currentPlan?.plan_id === plan.id;
  const hasActivePlan = !!currentPlan;

  const getPlanIcon = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('basic')) return Star;
    if (name.includes('pro')) return Zap;
    if (name.includes('enterprise')) return Crown;
    return Star;
  };

  const getPlanColor = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('basic')) return 'bg-slate-100 text-slate-800 border-slate-200';
    if (name.includes('pro')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (name.includes('enterprise')) return 'bg-purple-100 text-purple-800 border-purple-200';
    return 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const Icon = getPlanIcon(plan.name);
  const monthlyPrice = plan.price_monthly;
  const yearlyPrice = plan.price_yearly;
  const yearlyDiscount = (monthlyPrice * 12) - yearlyPrice;
  const yearlyDiscountPercent = Math.round((yearlyDiscount / (monthlyPrice * 12)) * 100);

  // Calculate if it's a popular plan (typically pro)
  const isPopular = plan.name.toLowerCase().includes('pro');

  return (
    <Card className={cn(
      "relative flex flex-col h-full transition-all duration-300 hover:shadow-lg",
      isCurrentPlan && "ring-2 ring-primary shadow-lg",
      isPopular && "border-primary/50"
    )}>
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-primary text-primary-foreground">Plano Atual</Badge>
        </div>
      )}
      
      {isPopular && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-blue-600 text-white">Mais Popular</Badge>
        </div>
      )}

      <CardHeader className="text-center pb-3">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-primary to-primary/80">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <CardTitle className="text-xl">{plan.name}</CardTitle>
        </div>
        
        <Badge variant="secondary" className={getPlanColor(plan.name)}>
          Sistema Individual
        </Badge>
        
        <CardDescription className="mt-2">{plan.description}</CardDescription>

        <Separator className="my-4" />

        {/* Recursos Padrão */}
        <DefaultFeaturesSection variant="default" />

        {/* Limites do Plano */}
        {(() => {
          const systemConfig = SYSTEM_FIELD_CONFIG[systemCode as keyof typeof SYSTEM_FIELD_CONFIG];
          
          if (!systemConfig) return null;
          
          const relevantLimits = Object.entries(plan.features.limits || {})
            .filter(([key]) => (systemConfig.limits as readonly string[]).includes(key))
            .filter(([, value]) => value !== undefined && value !== null);
          
          if (relevantLimits.length === 0) return null;
          
          return (
            <>
              <Separator className="my-4" />
              <div className="space-y-3">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <div className="w-1 h-4 bg-gradient-to-b from-primary to-primary/80 rounded-full" />
                  Limites do Plano
                </h4>
                <div className="space-y-2">
                  {relevantLimits.map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-sm py-1">
                      <span className="text-muted-foreground">
                        {systemConfig?.labels?.[key] || FEATURE_NAMES[key as keyof typeof FEATURE_NAMES] || key}
                      </span>
                      <span className="font-medium">
                        {value === 999999 ? (
                          <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/20">
                            Ilimitado
                          </Badge>
                        ) : (
                          (value as number).toLocaleString()
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()}

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-center gap-2">
            <span className="text-3xl font-bold text-primary">{formatCurrency(monthlyPrice)}</span>
            <span className="text-muted-foreground">/mês</span>
          </div>
          
          <div className="text-sm space-y-1">
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <span>ou {formatCurrency(yearlyPrice)}/ano</span>
              {yearlyDiscount > 0 && (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                  -{yearlyDiscountPercent}% OFF
                </Badge>
              )}
            </div>
            {yearlyDiscount > 0 && (
              <div className="flex items-center justify-center gap-1 text-xs text-green-600">
                <Gift className="w-3 h-3" />
                <span>2 meses de desconto no anual</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardFooter className="flex gap-2 mt-auto pt-6">
        {isCurrentPlan ? (
          <Button className="w-full" disabled>
            Plano Atual
          </Button>
        ) : (
          <>
            <Button
              onClick={() => onSelect('monthly')}
              disabled={disabled}
              className="flex-1"
              variant={isPopular ? "default" : "outline"}
            >
              Mensal
            </Button>
            <Button
              onClick={() => onSelect('yearly')}
              disabled={disabled}
              variant="outline"
              className="flex-1 relative"
            >
              <div className="flex flex-col items-center">
                <span>Anual</span>
                {yearlyDiscount > 0 && (
                  <span className="text-xs text-green-600">
                    -{yearlyDiscountPercent}%
                  </span>
                )}
              </div>
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}